import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialDayPlanState } from 'store/initial-states';
import {
  FileImportEvent,
  FileImportEventSeries,
  FileImportUnscheduledEntry
} from 'interfaces/day-plan.interface';
import { DayPlanTodaySegment, SORT_MODE, UnscheduledPromoteKind } from 'enums';
import {
  normalizeFileImportEventSeries,
  normalizeFileImportEvents,
  normalizeFileImportUnscheduledEntries,
  resolveUnscheduledPromotion
} from 'utils/day-plan/index.util';
import {
  commitFileImport,
  dismissEvent,
  fetchCalendarEvents,
  fetchDayPlan,
  fetchDayPlanOneOffEvents,
  fetchDayPlanTaskCatalog,
  fetchEventColours,
  fetchEventSeriesForDay,
  fetchRoutineProgress,
  importFileFromKey,
  planTask,
  setEventColor,
  undismissEvent
} from './extra';

const clearFileImportDrafts = (state: typeof initialDayPlanState) => {
  state.isReviewOpen = false;
  state.fileImportEvents = [];
  state.fileImportEventSeries = [];
  state.fileImportUnscheduledEntries = [];
  state.fileImportTruncated = false;
  state.fileImportCommitProgress = null;
};

const applyUnscheduledPromotion = (
  state: typeof initialDayPlanState,
  index: number,
  entry: FileImportUnscheduledEntry
) => {
  const result = resolveUnscheduledPromotion(entry);
  if (result.kind === UnscheduledPromoteKind.STAY) {
    state.fileImportUnscheduledEntries[index] = result.entry;
    return;
  }
  state.fileImportUnscheduledEntries =
    state.fileImportUnscheduledEntries.filter(
      (_row, rowIndex) => rowIndex !== index
    );
  if (result.kind === UnscheduledPromoteKind.EVENT) {
    state.fileImportEvents.push(result.event);
    return;
  }
  state.fileImportEventSeries.push(result.series);
};

const dayPlan = createSlice({
  name: 'day-plan',
  initialState: initialDayPlanState,
  reducers: {
    setSelectedDay: (state, { payload }: PayloadAction<string>) => {
      if (state.selectedDay !== payload) {
        state.routineProgress = null;
      }
      state.selectedDay = payload;
    },
    closeReview: (state) => {
      clearFileImportDrafts(state);
    },
    setFileImportCommitProgress: (
      state,
      { payload }: PayloadAction<{ completed: number; total: number } | null>
    ) => {
      state.fileImportCommitProgress = payload;
    },
    updateFileImportEvent: (
      state,
      { payload }: PayloadAction<{ index: number; event: FileImportEvent }>
    ) => {
      if (state.fileImportEvents[payload.index]) {
        state.fileImportEvents[payload.index] = payload.event;
      }
    },
    removeFileImportEvent: (state, { payload }: PayloadAction<number>) => {
      state.fileImportEvents = state.fileImportEvents.filter(
        (_event, index) => index !== payload
      );
    },
    updateFileImportEventSeries: (
      state,
      {
        payload
      }: PayloadAction<{ index: number; series: FileImportEventSeries }>
    ) => {
      if (state.fileImportEventSeries[payload.index]) {
        state.fileImportEventSeries[payload.index] = payload.series;
      }
    },
    removeFileImportEventSeries: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.fileImportEventSeries = state.fileImportEventSeries.filter(
        (_row, index) => index !== payload
      );
    },
    updateFileImportUnscheduledEntry: (
      state,
      {
        payload
      }: PayloadAction<{ index: number; entry: FileImportUnscheduledEntry }>
    ) => {
      if (state.fileImportUnscheduledEntries[payload.index] == null) {
        return;
      }
      applyUnscheduledPromotion(state, payload.index, payload.entry);
    },
    removeFileImportUnscheduledEntry: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.fileImportUnscheduledEntries =
        state.fileImportUnscheduledEntries.filter(
          (_row, index) => index !== payload
        );
    },
    setTaskCatalogSortMode: (state, { payload }: PayloadAction<SORT_MODE>) => {
      state.taskCatalogSortMode = payload;
    },
    setTaskCatalogSearchQuery: (state, { payload }: PayloadAction<string>) => {
      state.taskCatalogSearchQuery = payload;
    },
    setTodayPlanSegment: (
      state,
      { payload }: PayloadAction<DayPlanTodaySegment>
    ) => {
      state.todayPlanSegment = payload;
    },
    clearCalendarEvents: (state) => {
      state.calendarEvents = [];
      state.isLoadingEvents = false;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDayPlan.pending, (state) => {
        state.isLoadingDayPlan = true;
        state.tasks = [];
        state.dismissedEventIds = [];
      })
      .addCase(fetchDayPlan.fulfilled, (state, { payload, meta }) => {
        state.isLoadingDayPlan = false;
        if (meta.arg !== state.selectedDay) {
          return;
        }
        if (payload) {
          state.tasks = payload.tasks ?? [];
          state.dismissedEventIds = payload.dismissed_event_ids ?? [];
        }
      })
      .addCase(fetchDayPlan.rejected, (state, { meta }) => {
        state.isLoadingDayPlan = false;
        if (meta.arg === state.selectedDay) {
          state.tasks = [];
          state.dismissedEventIds = [];
        }
      })
      .addCase(fetchRoutineProgress.fulfilled, (state, { payload, meta }) => {
        if (meta.arg.date !== state.selectedDay) {
          return;
        }
        state.routineProgress = payload ?? null;
      })
      .addCase(fetchRoutineProgress.rejected, (state, { meta }) => {
        if (meta.arg.date === state.selectedDay) {
          state.routineProgress = null;
        }
      })
      .addCase(fetchEventColours.fulfilled, (state, { payload }) => {
        if (payload !== null) {
          state.eventColors = payload;
        }
      })
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.isLoadingEvents = true;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, { payload, meta }) => {
        state.isLoadingEvents = false;
        if (meta.arg.day !== state.selectedDay) {
          return;
        }
        state.calendarEvents = payload;
      })
      .addCase(fetchCalendarEvents.rejected, (state, { meta }) => {
        state.isLoadingEvents = false;
        if (meta.arg.day === state.selectedDay) {
          state.calendarEvents = [];
        }
      })
      .addCase(fetchEventSeriesForDay.pending, (state) => {
        state.isLoadingEventSeries = true;
      })
      .addCase(fetchEventSeriesForDay.fulfilled, (state, { payload, meta }) => {
        state.isLoadingEventSeries = false;
        if (meta.arg !== state.selectedDay) {
          return;
        }
        state.eventSeriesOccurrences = payload.events;
      })
      .addCase(fetchEventSeriesForDay.rejected, (state, { meta }) => {
        state.isLoadingEventSeries = false;
        if (meta.arg === state.selectedDay) {
          state.eventSeriesOccurrences = [];
        }
      })
      .addCase(fetchDayPlanOneOffEvents.pending, (state) => {
        state.isLoadingDayPlanOneOffEvents = true;
      })
      .addCase(
        fetchDayPlanOneOffEvents.fulfilled,
        (state, { payload, meta }) => {
          state.isLoadingDayPlanOneOffEvents = false;
          if (meta.arg !== state.selectedDay) {
            return;
          }
          state.dayPlanOneOffEvents = payload.events;
        }
      )
      .addCase(fetchDayPlanOneOffEvents.rejected, (state, { meta }) => {
        state.isLoadingDayPlanOneOffEvents = false;
        if (meta.arg === state.selectedDay) {
          state.dayPlanOneOffEvents = [];
        }
      })
      .addCase(planTask.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.tasks = state.tasks.filter((task) => task.id !== payload.id);
        if (payload.planned_date === state.selectedDay) {
          state.tasks.push(payload);
        }
      })
      .addCase(dismissEvent.fulfilled, (state, { payload }) => {
        if (payload && !state.dismissedEventIds.includes(payload)) {
          state.dismissedEventIds.push(payload);
        }
      })
      .addCase(undismissEvent.fulfilled, (state, { payload }) => {
        if (payload) {
          state.dismissedEventIds = state.dismissedEventIds.filter(
            (id) => id !== payload
          );
        }
      })
      .addCase(setEventColor.fulfilled, (state, { payload }) => {
        if (payload) {
          state.eventColors[payload.event_id] = payload.colour;
        }
      })
      .addCase(importFileFromKey.pending, (state) => {
        state.isImportingFile = true;
      })
      .addCase(importFileFromKey.fulfilled, (state, { payload }) => {
        state.isImportingFile = false;
        state.fileImportEvents = normalizeFileImportEvents(payload.events);
        state.fileImportEventSeries = normalizeFileImportEventSeries(
          payload.event_series
        );
        state.fileImportUnscheduledEntries =
          normalizeFileImportUnscheduledEntries(payload.unscheduled_entries);
        state.fileImportTruncated = payload.truncated;
        state.isReviewOpen =
          payload.events.length > 0 ||
          payload.event_series.length > 0 ||
          payload.unscheduled_entries.length > 0;
      })
      .addCase(importFileFromKey.rejected, (state) => {
        state.isImportingFile = false;
      })
      .addCase(commitFileImport.pending, (state, { meta }) => {
        const seriesCount = meta.arg.eventSeries?.length ?? 0;
        state.fileImportCommitProgress = {
          completed: 0,
          total: seriesCount + meta.arg.events.length
        };
      })
      .addCase(commitFileImport.fulfilled, (state, { payload }) => {
        state.fileImportCommitProgress = null;
        if (payload?.success === true) {
          clearFileImportDrafts(state);
        }
      })
      .addCase(commitFileImport.rejected, (state) => {
        state.fileImportCommitProgress = null;
      })
      .addCase(fetchDayPlanTaskCatalog.pending, (state) => {
        state.isLoadingTaskCatalog = true;
      })
      .addCase(fetchDayPlanTaskCatalog.fulfilled, (state, { payload }) => {
        state.isLoadingTaskCatalog = false;
        state.taskCatalog = payload.tasks;
      })
      .addCase(fetchDayPlanTaskCatalog.rejected, (state) => {
        state.isLoadingTaskCatalog = false;
      });
  }
});

export const {
  setSelectedDay,
  closeReview,
  setFileImportCommitProgress,
  updateFileImportEvent,
  removeFileImportEvent,
  updateFileImportEventSeries,
  removeFileImportEventSeries,
  updateFileImportUnscheduledEntry,
  removeFileImportUnscheduledEntry,
  setTaskCatalogSortMode,
  setTaskCatalogSearchQuery,
  setTodayPlanSegment,
  clearCalendarEvents
} = dayPlan.actions;

export default dayPlan.reducer;
